import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import notification from "common/utils/notification"
import ArticuloService from "features/Inventario/services/ArticuloService"
import {
  findNode,
  getAllDescendentCategorias,
} from "features/Inventario/utils/categoriasTreeUtils"
import mapArticulosToSendApi from "features/Inventario/utils/mapArticulosToSendToApi"
import pushArticulosToArticulosSelected from "features/Inventario/utils/pushArticulosToArticulosSelected"
import removeCategoriasFromArticulosSelected from "features/Inventario/utils/removeCategoriasFromArticulosSelected"
import updateCurrentArticulos from "features/Inventario/utils/updateCurrentArticulos"
import i18next from "i18next"
import CatalogosService from "../../services/CatalogoService"
import CategoriaService from "../../services/CategoriaService"
import mapArticulosByCategoria from "../../utils/mapArticulos"
import mergeArticulosSelected from "../../utils/mergeArticulosSelected"

export const getCatalogos = createAsyncThunk(
  "catalogos/getAll",
  async (nombre: $TSMigrationFixMe) => {
    const { data } = await CatalogosService.get({ nombre })
    return data
  }
)

export const getArticulosByCategoria = createAsyncThunk(
  "catalogos/getArticulosByCategoria",
  async (_: $TSMigrationFixMe, { getState }: $TSMigrationFixMe) => {
    const { conceptoSearch: concepto, categoriasSelected: categoriasId } =
      getState().catalogos

    const { data } = await ArticuloService.getByCategoriaFilters({
      concepto,
      categoriasId,
    })

    return data
  }
)

export const getCategoriasTree = createAsyncThunk(
  "catalogos/getCategoriasTree",
  async () => {
    const categorias = await CategoriaService.getAllPadres()
    return categorias.data
  }
)

export const selectCategoriasTree = createAsyncThunk(
  "catalogos/selectAllArticulosFromCategoria",
  async (nodeSelected: $TSMigrationFixMe, { getState }: $TSMigrationFixMe) => {
    const { categoriasTree } = getState().catalogos
    const newIsSelected = !nodeSelected.isSelected

    const { node } = findNode({
      categorias: categoriasTree,
      id: nodeSelected.id,
    })

    const categoriasSelected = getAllDescendentCategorias(node)
    const { data } = await CategoriaService.getByCategoriasId(
      categoriasSelected
    )

    return {
      categorias: data,
      newIsSelected,
      categoriasSelected,
    }
  }
)

export const createCatalogo = createAsyncThunk(
  "catalogos/createCatalogo",
  async (
    { nombre, imageFile }: $TSMigrationFixMe,
    { getState }: $TSMigrationFixMe
  ) => {
    const { articulosByCategoria, articulosSelected } = getState().catalogos

    const { articulosId, categoriasId } = mapArticulosToSendApi({
      articulosByCategoria,
      articulosSelected,
    })

    await CatalogosService.post({
      articulosId,
      categoriasId,
      nombre,
      imageFile,
    })
  }
)

export const deleteCatalogo = createAsyncThunk(
  "catalogos/deleteOne",
  async (id: $TSMigrationFixMe, { dispatch }: $TSMigrationFixMe) => {
    await CatalogosService.deleteById(id)
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    dispatch(getCatalogos())
  }
)

export const getCatalogo = createAsyncThunk(
  "catalogos/getCatalogo",
  async (id: $TSMigrationFixMe) => {
    const { data } = await CatalogosService.getById(id)
    return data
  }
)

export const getCatalogoEncrypted = createAsyncThunk(
  "catalogos/getCatalogoEncrypted",
  async (id: $TSMigrationFixMe) => {
    const { data } = await CatalogosService.getByIdEncrypted(id)
    return data
  }
)

export const getArticulosSelected = createAsyncThunk(
  "catalogos/getArticulosSelected",
  async (catalogoId: $TSMigrationFixMe) => {
    const allCategoriasPromise = ArticuloService.getByCategoriaFilters()
    const articulosSelectedPromise =
      ArticuloService.getArticulosGroupedByCategoria({
        catalogoId,
      })

    const [allCategorias, articulosSelected] = await Promise.all([
      allCategoriasPromise,
      articulosSelectedPromise,
    ])

    return {
      allCategorias: allCategorias.data,
      articulosSelected: articulosSelected.data,
    }
  }
)

export const updateCatalogo = createAsyncThunk(
  "catalogos/update",
  async (
    { catalogoId, imageFile, nombre, ficheroId }: $TSMigrationFixMe,
    { getState }: $TSMigrationFixMe
  ) => {
    const { articulosByCategoria, articulosSelected } = getState().catalogos

    const { articulosId, categoriasId } = mapArticulosToSendApi({
      articulosByCategoria,
      articulosSelected,
    })

    await CatalogosService.update({
      id: catalogoId,
      articulosId,
      categoriasId,
      imageFile,
      ficheroId,
      nombre,
    })
  }
)

export const shareCatalogo = createAsyncThunk(
  "catalogos/share",
  async ({ catalogoId, emails }: $TSMigrationFixMe) => {
    await CatalogosService.share({ catalogoId, emails })
    notification.success({
      body: `${i18next.t("notification.catalogCorrectShared")}`,
    })
  }
)

const initialState = {
  catalogo: null,
  catalogos: [],
  categorias: [],
  articulosByCategoria: {},
  articulosSelected: {},
  categoriasSelected: [],
  categoriasTree: [],
  conceptoSearch: "",
  fetchingArticulos: false,
  fetchingCatalogos: false,
  fetchingCatalogo: false,
  fetchingCatalogosTree: false,
  sharingCatalogo: false,
  updatingCatalogo: false,
  creatingCatalogo: false,
  catalogoSuccessCreated: false,
  catalogoSuccessUpdated: false,
  catalogoDeletedSuccess: false,
}

export const catalogosSlice = createSlice({
  name: "catalogos",
  initialState,
  reducers: {
    toggleSelectionArticulo(
      state: $TSMigrationFixMe,
      { payload }: $TSMigrationFixMe
    ) {
      const { categoriaNombre } = payload
      const articulos = state.articulosByCategoria[categoriaNombre]?.articulos

      const index = articulos?.findIndex(
        (art: $TSMigrationFixMe) => art.id === payload.id
      )
      const newIsSelected = !articulos[index].isSelected
      articulos[index].isSelected = newIsSelected

      state.articulosSelected = mergeArticulosSelected({
        articulosSelected: state.articulosSelected,
        articulos: state.articulosByCategoria,
      })
    },
    selectAllArticulos(state: $TSMigrationFixMe, { payload: selectAll }) {
      const { articulosByCategoria } = state
      Object.keys(articulosByCategoria).forEach((categoriaName) => {
        articulosByCategoria[categoriaName]?.articulos?.forEach(
          (_: $TSMigrationFixMe, index: $TSMigrationFixMe) => {
            state.articulosByCategoria[categoriaName].articulos[
              index
            ].isSelected = selectAll
          }
        )
      })

      state.articulosSelected = mergeArticulosSelected({
        articulosSelected: state.articulosSelected,
        articulos: articulosByCategoria,
      })
    },
    deleteArticuloFromCategoriaSelected(
      state: $TSMigrationFixMe,
      { payload: { categoriaNombre, id } }: $TSMigrationFixMe
    ) {
      const articulosSelected =
        state.articulosSelected[categoriaNombre]?.articulos
      const index = articulosSelected.findIndex(
        (articulo: $TSMigrationFixMe) => articulo.id === id
      )

      articulosSelected.splice(index, 1)
      if (!articulosSelected.length) {
        delete state.articulosSelected[categoriaNombre]
      }

      state.articulosByCategoria = updateCurrentArticulos({
        currentArticulos: state.articulosByCategoria,
        categoriasSelected: state.articulosSelected,
      })
    },
    unSelectCategoriasTree(
      state: $TSMigrationFixMe,
      { payload }: $TSMigrationFixMe
    ) {
      const categoriasSelected = getAllDescendentCategorias(payload)
      const newArticulosSelected = removeCategoriasFromArticulosSelected({
        articulosSelected: state.articulosSelected,
        categorias: categoriasSelected,
      })

      return {
        ...state,
        articulosSelected: newArticulosSelected,
        articulosByCategoria: updateCurrentArticulos({
          categoriasSelected: newArticulosSelected,
          currentArticulos: state.articulosByCategoria,
        }),
      }
    },
    setConceptoSearch(
      state: $TSMigrationFixMe,
      { payload }: $TSMigrationFixMe
    ) {
      state.conceptoSearch = payload
    },
    selectCategorias(state: $TSMigrationFixMe, { payload }: $TSMigrationFixMe) {
      state.categoriasSelected = payload
    },
    clearCatalogosState: () => initialState,
    setCategoriasTree(
      state: $TSMigrationFixMe,
      { payload }: $TSMigrationFixMe
    ) {
      state.categoriasTree = payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getArticulosByCategoria.pending, (state) => {
        state.fetchingArticulos = true
      })
      .addCase(getArticulosByCategoria.rejected, (state) => {
        state.fetchingArticulos = false
      })
      .addCase(getArticulosByCategoria.fulfilled, (state, { payload }) => {
        state.fetchingArticulos = false
        const mappedArticulos = mapArticulosByCategoria(
          payload,
          state.articulosSelected
        )

        state.articulosByCategoria = updateCurrentArticulos({
          categoriasSelected: state.articulosSelected,
          currentArticulos: mappedArticulos,
        })
      })

      .addCase(getCategoriasTree.pending, (state) => {
        state.fetchingCatalogosTree = true
      })
      .addCase(getCategoriasTree.rejected, (state) => {
        state.fetchingCatalogosTree = false
      })
      .addCase(getCategoriasTree.fulfilled, (state, { payload }) => {
        state.fetchingCatalogosTree = false
        state.categoriasTree = payload?.map((categoria: $TSMigrationFixMe) => ({
          ...categoria,
          isSelected: false,
        }))
      })

      .addCase(
        selectCategoriasTree.fulfilled,
        (state, { payload: { categorias } }) => {
          const newArticulosSelected = pushArticulosToArticulosSelected({
            articulosSelected: state.articulosSelected,
            categorias,
          })

          return {
            ...state,
            articulosSelected: newArticulosSelected,
            articulosByCategoria: updateCurrentArticulos({
              categoriasSelected: newArticulosSelected,
              currentArticulos: state.articulosByCategoria,
            }),
          }
        }
      )

      .addCase(createCatalogo.pending, (state) => {
        state.catalogoSuccessCreated = false
        state.creatingCatalogo = true
      })
      .addCase(createCatalogo.rejected, (state) => {
        state.creatingCatalogo = false
      })
      .addCase(createCatalogo.fulfilled, (state) => {
        state.catalogoSuccessCreated = true
        state.creatingCatalogo = false
      })

      .addCase(getCatalogos.pending, (state) => {
        state.catalogoDeletedSuccess = false
        state.fetchingCatalogos = true
      })
      .addCase(getCatalogos.rejected, (state) => {
        state.fetchingCatalogos = false
      })
      .addCase(getCatalogos.fulfilled, (state, { payload }) => {
        state.fetchingCatalogos = false
        state.catalogos = payload
      })

      .addCase(deleteCatalogo.pending, (state) => {
        state.catalogoDeletedSuccess = false
      })
      .addCase(deleteCatalogo.fulfilled, (state) => {
        state.catalogoDeletedSuccess = true
        notification.success({
          body: `${i18next.t("notification.catalogCorrectDelete")}`,
        })
      })

      .addCase(getCatalogo.pending, (state) => {
        state.fetchingCatalogo = true
      })
      .addCase(getCatalogo.fulfilled, (state, { payload }) => {
        state.fetchingCatalogo = false
        state.categorias = payload?.categorias
        state.catalogo = payload
      })

      .addCase(getCatalogoEncrypted.pending, (state) => {
        state.fetchingCatalogo = true
      })
      .addCase(getCatalogoEncrypted.fulfilled, (state, { payload }) => {
        state.fetchingCatalogo = false
        state.categorias = payload?.categorias
        state.catalogo = payload
      })

      .addCase(getArticulosSelected.pending, (state) => {
        state.fetchingArticulos = true
      })
      .addCase(
        getArticulosSelected.fulfilled,
        (state, { payload: { allCategorias, articulosSelected } }) => {
          state.fetchingArticulos = false

          state.articulosSelected = mapArticulosByCategoria(articulosSelected)
          state.articulosByCategoria = updateCurrentArticulos({
            categoriasSelected: state.articulosSelected,
            currentArticulos: mapArticulosByCategoria(allCategorias),
          })
        }
      )

      .addCase(updateCatalogo.pending, (state) => {
        state.updatingCatalogo = true
      })
      .addCase(updateCatalogo.rejected, (state) => {
        state.updatingCatalogo = false
      })
      .addCase(updateCatalogo.fulfilled, (state) => {
        state.updatingCatalogo = false
        state.catalogoSuccessUpdated = true
      })

      .addCase(shareCatalogo.pending, (state) => {
        state.sharingCatalogo = true
      })
      .addCase(shareCatalogo.rejected, (state) => {
        state.sharingCatalogo = false
      })
      .addCase(shareCatalogo.fulfilled, (state) => {
        state.sharingCatalogo = false
      })
  },
})

export const {
  toggleSelectionArticulo,
  selectAllArticulos,
  setConceptoSearch,
  clearCatalogosState,
  selectCategorias,
  setCategoriasTree,
  deleteArticuloFromCategoriaSelected,
  unSelectCategoriasTree,
} = catalogosSlice.actions
